import type { Action, PayloadAction } from '@reduxjs/toolkit';

export type CommonState = {
  refresh: Record<string, never>;
  isLoading: boolean;
  /** ProcessingLoaderの表示フラグ */
  isProcessing: boolean;
  isSubmitting: boolean;
  keywordRefresh: Record<string, never>;
  selectedTagIds: number[];
  /** 建物選択用建物リスト */
  propertyList: PropertyListWithIsFavorite[];
  /** タグ選択用建物リスト */
  propertyListFilteredByTagIds: PropertyListWithIsFavorite[];
  selectedPropertyId: number | null;
  profile: Profile | null;
  /** 機器(業務)種別、機器(業務)科目のマスタ情報 */
  equipmentMst: EquipmentMst;
  runtimeError: RuntimeError | null;
  /** 建物全体公開設定 */
  isPublic: boolean;
};

export enum CommonActionTypes {
  REFRESH = 'template/refresh',
  LOADING = 'common/set/isLoading',
  PROCESSING = 'common/set/isProcessing',
  SUBMITTING = 'common/set/isSubmitting',
  KEYWORD_REFRESH = 'template/keywordRefresh',
  SET_PROPERTY_LIST = 'common/set/propertyList',
  SET_PROPERTY_LIST_FILTERED_BY_TAG_IDS = 'common/set/propertyListFilteredByTagIds',
  SET_PROPERTY_ID = 'common/set/selectedPropertyId',
  SET_SELECTED_TAG_IDS = 'common/set/selectedTagIds',
  SET_PROFILE = 'common/set/profile',
  SET_EQUIPMENT_MST = 'common/set/equipmentMst',
  SET_RUNTIME_ERROR = 'common/set/runtimeError',
  SET_IS_PUBLIC = 'common/set/isPublic',
}

export type CommonAction =
  | Action<CommonActionTypes.REFRESH>
  | PayloadAction<boolean, CommonActionTypes.LOADING>
  | PayloadAction<boolean, CommonActionTypes.PROCESSING>
  | PayloadAction<boolean, CommonActionTypes.SUBMITTING>
  | Action<CommonActionTypes.KEYWORD_REFRESH>
  | PayloadAction<PropertyListWithIsFavorite[], CommonActionTypes.SET_PROPERTY_LIST>
  | PayloadAction<
      PropertyListWithIsFavorite[],
      CommonActionTypes.SET_PROPERTY_LIST_FILTERED_BY_TAG_IDS
    >
  | PayloadAction<number[], CommonActionTypes.SET_SELECTED_TAG_IDS>
  | PayloadAction<number | null, CommonActionTypes.SET_PROPERTY_ID>
  | PayloadAction<Profile | null, CommonActionTypes.SET_PROFILE>
  | PayloadAction<EquipmentMst, CommonActionTypes.SET_EQUIPMENT_MST>
  | PayloadAction<RuntimeError | null, CommonActionTypes.SET_RUNTIME_ERROR>
  | PayloadAction<boolean, CommonActionTypes.SET_IS_PUBLIC>;

// -- State Types ---------------------------------------------------------- //

export type Property = {
  id: number;
  name: string;
};

/** 監督者、作業者等のユーザー情報の基底となる型 */
export type UserBase = {
  /** ユーザID */
  user_id: number;
  /** ユーザ名 */
  user_name: string;
};

export type PropertyListWithIsFavorite = Property & { is_favorite: boolean };

export type Profile = {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  icon: string | null;
  isSystemAdmin: boolean;
  properties: {
    id: number;
    roleId: number; // 1: 作業者, 2: 管理者
  }[];
};

/** 機器(業務)種別、機器(業務)科目の型 */
export type EquipmentMst = {
  /** 機器(業務)種別のリスト */
  equipment_type_list: EquipmentTypeMst[];
  /** 機器(業務)科目のリスト */
  equipment_subject_list: EquipmentSubjectMst[];
};

/** 機器(業務)種別 */
export type EquipmentTypeMst = {
  /** 機器(業務)種別ID */
  mst_equipment_type_id: number;
  /** 機器(業務)種別名 */
  mst_equipment_type_name: string;
};

/** 機器(業務)科目 */
export type EquipmentSubjectMst = {
  /** 機器(業務)種別ID */
  mst_equipment_type_id: number;
  /** 機器(業務)科目ID */
  mst_equipment_subject_id: number;
  /** 機器(業務)科目名 */
  mst_equipment_subject_name: string;
};

export type RuntimeError = {
  error: Error;
  componentStack: string;
};

export type TargetMenuType =
  | 'property'
  | 'contract'
  | 'equipment'
  | 'floor'
  | '/plan/annual'
  | '/plan/monthly'
  | '/plan/daily'
  | 'checking'
  | 'inspection'
  | 'construction'
  | 'work'
  | 'archive'
  | '/inspection/checklist';

export type DestinationPathDataType = {
  [key in TargetMenuType]: {
    individual: string;
    whole: string;
    filtered_by_tag: string;
  };
};

// -- API Types ------------------------------------------------------------ //
type APIProfile = {
  readonly user_id: number;
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly is_system_role: boolean;
  readonly file_url: string | null;
  readonly property_list: { readonly property_id: number; readonly property_role_id: number }[];
};

export type APIGetPropertyListResponse = {
  message: string;
  result: { property_id: number; property_name: string; is_favorite: boolean }[];
};

export type APIPostRegisterAndRemovePropertyFavoriteResponse = {
  message: string;
  result: {
    message: string;
    result: {
      property_id: number;
      property_name: string;
      is_favorite: boolean;
    };
  };
};

export type APIGetProfileResponse = {
  message: string;
  result: APIProfile;
};

export type APIGetEquipmentMstResponse = {
  message: string;
  result: EquipmentMst;
};

export type APISettings = {
  readonly is_public: boolean;
};

export type APIGetSettingsResponse = {
  result: APISettings;
};

/** @summary ダウンロードAPIのレスポンス型 */
export type APIGetDownloadResponse = {
  result: {
    file_content: string;
    file_name: string;
  };
};

/**
 * @summary 一覧画面のリスト取得で共通して使用できるリクエストパラメータ
 */
export type ListRequestParams = {
  /** 標準検索文言 */
  keyword?: string;
  /** ページ番号 */
  page: number;
  /** ソート番号 */
  sort: number;
  /** 1ページ当たりのデータ件数 */
  size_per_page?: number;
};

/**
 * @summary 一覧画面のPagerの表示で使用する情報
 */
export type DisplayParams = {
  /** 全件数 */
  total: number;
  /** 全体ページ数 */
  total_page: number;
  /** 現在のページ */
  current_page: number;
  /** ソート番号 */
  sort: number;
};
